<template>
  <div class="system-monitoring">
    <!-- 刷新按钮 -->
    <div class="refresh-bar" v-fade-in>
      <span class="last-update">上次更新: {{ lastUpdateTime }}</span>
      <button class="refresh-btn" @click="loadData" :disabled="loading">
        <span v-if="loading">刷新中...</span>
        <span v-else>刷新数据</span>
      </button>
      <label class="auto-refresh">
        <input type="checkbox" v-model="autoRefresh" @change="toggleAutoRefresh" />
        自动刷新 (5秒)
      </label>
    </div>

    <!-- 概览卡片 -->
    <div class="stats-grid">
      <div class="stat-card" v-motion="{ initial: { opacity: 0, y: 40, scale: 0.8 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { type: 'spring', stiffness: 250, damping: 20, delay: 0.1 } }">
        <div class="stat-icon users-icon pulse-icon">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
          </svg>
        </div>
        <div class="stat-info">
          <div class="stat-value heartbeat">{{ animatedStats.totalConnections }}</div>
          <div class="stat-label">总连接数</div>
        </div>
      </div>

      <div class="stat-card" v-motion="{ initial: { opacity: 0, y: 40, scale: 0.8 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { type: 'spring', stiffness: 250, damping: 20, delay: 0.2 } }">
        <div class="stat-icon agent-icon pulse-icon">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
          </svg>
        </div>
        <div class="stat-info">
          <div class="stat-value heartbeat">{{ animatedStats.agentCount }}</div>
          <div class="stat-label">在线用户</div>
        </div>
      </div>

      <div class="stat-card" v-motion="{ initial: { opacity: 0, y: 40, scale: 0.8 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { type: 'spring', stiffness: 250, damping: 20, delay: 0.3 } }">
        <div class="stat-icon customer-icon pulse-icon">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
        </div>
        <div class="stat-info">
          <div class="stat-value heartbeat">{{ animatedStats.customerCount }}</div>
          <div class="stat-label">在线鱼</div>
        </div>
      </div>

      <div class="stat-card" v-motion="{ initial: { opacity: 0, y: 40, scale: 0.8 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { type: 'spring', stiffness: 250, damping: 20, delay: 0.4 } }">
        <div class="stat-icon memory-icon pulse-icon">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M15 9H9v6h6V9zm-2 4h-2v-2h2v2zm8-2V9h-2V7c0-1.1-.9-2-2-2h-2V3h-2v2h-2V3H9v2H7c-1.1 0-2 .9-2 2v2H3v2h2v2H3v2h2v2c0 1.1.9 2 2 2h2v2h2v-2h2v2h2v-2h2c1.1 0 2-.9 2-2v-2h2v-2h-2v-2h2zm-4 6H7V7h10v10z"/>
          </svg>
        </div>
        <div class="stat-info">
          <div class="stat-value heartbeat">{{ animatedStats.memoryPercent }}%</div>
          <div class="stat-label">内存占用</div>
        </div>
      </div>
    </div>

    <!-- 服务器状态 -->
    <div class="section-card line-draw" v-motion="{ initial: { opacity: 0, x: -60 }, animate: { opacity: 1, x: 0 }, transition: { type: 'spring', stiffness: 200, damping: 22, delay: 0.1 } }">
      <h3>服务器状态</h3>
      <div class="server-info">
        <div class="info-row">
          <span class="info-label">运行时间</span>
          <span class="info-value">{{ server.uptime || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">内存使用</span>
          <span class="info-value">{{ server.memoryUsed || '-' }} / {{ server.memoryMax || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">CPU 核心数</span>
          <span class="info-value">{{ server.processors || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">活动线程数</span>
          <span class="info-value">{{ server.threadCount || '-' }}</span>
        </div>
        <div class="memory-bar">
          <div class="memory-progress" :style="{ width: (server.memoryPercent || 0) + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- 在线用户列表 -->
    <div class="section-card line-draw" v-motion="{ initial: { opacity: 0, x: -60 }, animate: { opacity: 1, x: 0 }, transition: { type: 'spring', stiffness: 200, damping: 22, delay: 0.15 } }">
      <h3>在线用户 ({{ online.agentCount || 0 }})</h3>
      <div class="user-list" v-if="online.agents && online.agents.length > 0">
        <div class="user-item" v-for="agent in online.agents" :key="agent.sessionId">
          <div class="user-avatar agent">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
            </svg>
          </div>
          <div class="user-info">
            <div class="user-name">{{ agent.account }}</div>
            <div class="user-detail">会话ID: {{ agent.sessionId }}</div>
          </div>
          <div class="user-status online"><span class="pulse-dot"></span>在线</div>
        </div>
      </div>
      <div class="empty-list" v-else>暂无在线用户</div>
    </div>

    <!-- 在线鱼列表 -->
    <div class="section-card line-draw" v-motion="{ initial: { opacity: 0, x: -60 }, animate: { opacity: 1, x: 0 }, transition: { type: 'spring', stiffness: 200, damping: 22, delay: 0.2 } }">
      <h3>在线鱼 ({{ online.customerCount || 0 }})</h3>
      <div class="user-list" v-if="online.customers && online.customers.length > 0">
        <div class="user-item" v-for="customer in online.customers" :key="customer.wsSessionId">
          <div class="user-avatar customer">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          <div class="user-info">
            <div class="user-name">{{ customer.customerName }}</div>
            <div class="user-detail">{{ customer.sessionKey }}</div>
          </div>
          <div class="user-status online"><span class="pulse-dot"></span>在线</div>
        </div>
      </div>
      <div class="empty-list" v-else>暂无在线鱼</div>
    </div>

    <!-- 域名访问管理 -->
    <div class="section-card line-draw" v-motion="{ initial: { opacity: 0, x: -60 }, animate: { opacity: 1, x: 0 }, transition: { type: 'spring', stiffness: 200, damping: 22, delay: 0.25 } }">
      <div class="section-header">
        <h3>域名访问管理 ({{ domains.length }})</h3>
        <button class="refresh-btn small" @click="loadDomains">刷新</button>
      </div>
      <div class="domain-list" v-if="domains.length > 0">
        <div class="domain-item" v-for="domain in domains" :key="domain.id">
          <div class="domain-info">
            <div class="domain-name">
              {{ domain.domain }}
              <span v-if="domain.isBlocked" class="blocked-badge">已拉黑</span>
            </div>
            <div class="domain-stats">
              <span>访问次数: {{ domain.accessCount }}</span>
              <span>首次访问: {{ formatDateTime(domain.firstAccessTime) }}</span>
              <span>最后访问: {{ formatDateTime(domain.lastAccessTime) }}</span>
            </div>
            <div class="domain-remark" v-if="domain.remark">
              <span class="remark-label">备注:</span> {{ domain.remark }}
            </div>
          </div>
          <div class="domain-actions">
            <button
              class="action-btn landing-btn"
              @click="openLandingModal(domain)"
              title="落地页">
              📄
            </button>
            <button
              class="action-btn remark-btn"
              @click="openRemarkModal(domain)"
              title="备注">
              📝
            </button>
            <button
              v-if="!domain.isBlocked"
              class="action-btn block-btn"
              @click="blockDomain(domain.id)"
              title="拉黑">
              🚫
            </button>
            <button
              v-else
              class="action-btn unblock-btn"
              @click="unblockDomain(domain.id)"
              title="解除拉黑">
              ✅
            </button>
            <button
              class="action-btn delete-btn"
              @click="deleteDomain(domain.id)"
              title="删除">
              🗑️
            </button>
          </div>
        </div>
      </div>
      <div class="empty-list" v-else>暂无域名访问记录</div>
    </div>

    <!-- 备注弹窗 -->
    <div class="modal-overlay" v-if="showRemarkModal" @click="closeRemarkModal">
      <div class="modal-box" @click.stop>
        <div class="modal-header">
          <h4>编辑备注</h4>
          <button class="modal-close" @click="closeRemarkModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>域名</label>
            <div class="domain-display">{{ currentDomain?.domain }}</div>
          </div>
          <div class="form-item">
            <label>备注</label>
            <textarea
              v-model="remarkText"
              class="form-textarea"
              placeholder="请输入备注信息（最多500字符）"
              maxlength="500"
              rows="4"
            ></textarea>
            <div class="char-count">{{ remarkText.length }}/500</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeRemarkModal">取消</button>
          <button class="btn btn-primary" @click="saveRemark">保存</button>
        </div>
      </div>
    </div>

    <!-- 落地页弹窗 -->
    <div class="modal-overlay" v-if="showLandingModal" @click="closeLandingModal">
      <div class="modal-box" @click.stop>
        <div class="modal-header">
          <h4>设置落地页</h4>
          <button class="modal-close" @click="closeLandingModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>域名</label>
            <div class="domain-display">{{ landingDomain?.domain }}</div>
          </div>
          <div class="form-item">
            <label>选择落地页模板</label>
            <select v-model="landingFile" class="form-select">
              <option value="">不设置</option>
              <option value="maintenance.html">系统维护中</option>
              <option value="notfound.html">页面不存在 (404)</option>
              <option value="blocked.html">访问受限</option>
              <option value="welcome.html">欢迎页面</option>
            </select>
          </div>
          <div class="form-item" v-if="landingFile">
            <label>预览链接</label>
            <div class="landing-url-row">
              <input
                type="text"
                class="form-input"
                :value="getPreviewUrl()"
                readonly
              />
              <button class="btn btn-secondary" @click="previewLanding">预览</button>
            </div>
          </div>
          <div class="form-item" v-if="landingFile">
            <label>访问链接</label>
            <div class="landing-url-row">
              <input
                type="text"
                class="form-input"
                :value="getLandingUrl()"
                readonly
              />
              <button class="btn btn-secondary" @click="copyLandingUrl">复制</button>
            </div>
            <div class="hint">用户访问此链接将自动跳转到选择的落地页</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeLandingModal">取消</button>
          <button class="btn btn-primary" @click="saveLandingFile">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/config/api'

export default {
  name: 'SystemMonitoring',
  data() {
    return {
      loading: false,
      autoRefresh: true,
      refreshTimer: null,
      lastUpdateTime: '-',
      online: {
        totalConnections: 0,
        agentCount: 0,
        customerCount: 0,
        agents: [],
        customers: []
      },
      server: {
        memoryUsed: '-',
        memoryMax: '-',
        memoryPercent: 0,
        processors: 0,
        uptime: '-',
        threadCount: 0
      },
      animatedStats: {
        totalConnections: 0,
        agentCount: 0,
        customerCount: 0,
        memoryPercent: 0
      },
      domains: [],
      showRemarkModal: false,
      currentDomain: null,
      remarkText: '',
      showLandingModal: false,
      landingDomain: null,
      landingFile: ''
    }
  },
  watch: {
    'online.totalConnections'(val) { this.animateNumber('totalConnections', val) },
    'online.agentCount'(val) { this.animateNumber('agentCount', val) },
    'online.customerCount'(val) { this.animateNumber('customerCount', val) },
    'server.memoryPercent'(val) { this.animateNumber('memoryPercent', val) }
  },
  mounted() {
    this.loadData()
    this.loadDomains()
    if (this.autoRefresh) {
      this.startAutoRefresh()
    }
  },
  beforeUnmount() {
    this.stopAutoRefresh()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await apiFetch('/api/admin/monitoring/dashboard')
        if (res.code === 0 && res.data) {
          if (res.data.online) {
            this.online = res.data.online
          }
          if (res.data.server) {
            this.server = res.data.server
          }
          this.lastUpdateTime = this.formatTime(new Date())
        }
      } catch (e) {
        console.error('加载监控数据失败', e)
      } finally {
        this.loading = false
      }
    },
    async loadDomains() {
      try {
        const res = await apiFetch('/api/admin/domains')
        if (res.code === 0) {
          this.domains = res.data || []
        }
      } catch (e) {
        console.error('加载域名列表失败', e)
      }
    },
    async blockDomain(id) {
      if (!confirm('确定要拉黑该域名吗？拉黑后该域名将无法访问系统。')) return
      try {
        const res = await apiFetch(`/api/admin/domains/${id}/block`, {
          method: 'POST'
        })
        if (res.code === 0) {
          alert('拉黑成功')
          this.loadDomains()
        } else {
          alert(res.msg || '拉黑失败')
        }
      } catch (e) {
        alert('拉黑失败')
      }
    },
    async unblockDomain(id) {
      if (!confirm('确定要解除拉黑吗？')) return
      try {
        const res = await apiFetch(`/api/admin/domains/${id}/unblock`, {
          method: 'POST'
        })
        if (res.code === 0) {
          alert('解除拉黑成功')
          this.loadDomains()
        } else {
          alert(res.msg || '解除拉黑失败')
        }
      } catch (e) {
        alert('解除拉黑失败')
      }
    },
    async deleteDomain(id) {
      if (!confirm('确定要删除该域名记录吗？')) return
      try {
        const res = await apiFetch(`/api/admin/domains/${id}`, {
          method: 'DELETE'
        })
        if (res.code === 0) {
          alert('删除成功')
          this.loadDomains()
        } else {
          alert(res.msg || '删除失败')
        }
      } catch (e) {
        alert('删除失败')
      }
    },
    openRemarkModal(domain) {
      this.currentDomain = domain
      this.remarkText = domain.remark || ''
      this.showRemarkModal = true
    },
    closeRemarkModal() {
      this.showRemarkModal = false
      this.currentDomain = null
      this.remarkText = ''
    },
    async saveRemark() {
      if (!this.currentDomain) return
      try {
        const res = await apiFetch(`/api/admin/domains/${this.currentDomain.id}/remark`, {
          method: 'PUT',
          body: JSON.stringify({ remark: this.remarkText })
        })
        if (res.code === 0) {
          alert('保存成功')
          this.closeRemarkModal()
          this.loadDomains()
        } else {
          alert(res.msg || '保存失败')
        }
      } catch (e) {
        alert('保存失败')
      }
    },
    // 落地页相关方法
    async openLandingModal(domain) {
      this.landingDomain = domain
      this.landingFile = domain.landingFile || ''
      this.showLandingModal = true
    },
    closeLandingModal() {
      this.showLandingModal = false
      this.landingDomain = null
      this.landingFile = ''
    },
    getPreviewUrl() {
      if (!this.landingFile) return ''
      return `${window.location.origin}/landing/${this.landingFile}`
    },
    getLandingUrl() {
      if (!this.landingDomain) return ''
      return `${window.location.origin}/api/landing/${encodeURIComponent(this.landingDomain.domain)}`
    },
    previewLanding() {
      const url = this.getPreviewUrl()
      if (url) window.open(url, '_blank')
    },
    copyLandingUrl() {
      const url = this.getLandingUrl()
      navigator.clipboard.writeText(url).then(() => {
        alert('链接已复制')
      }).catch(() => {
        alert('复制失败，请手动复制')
      })
    },
    async saveLandingFile() {
      if (!this.landingDomain) return
      try {
        const res = await apiFetch(`/api/admin/domains/${this.landingDomain.id}/landing-file`, {
          method: 'PUT',
          body: JSON.stringify({ landingFile: this.landingFile })
        })
        if (res.code === 0) {
          alert('落地页设置成功')
          this.closeLandingModal()
          this.loadDomains()
        } else {
          alert(res.msg || '保存失败')
        }
      } catch (e) {
        alert('保存失败')
      }
    },
    toggleAutoRefresh() {
      if (this.autoRefresh) {
        this.startAutoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.refreshTimer = setInterval(() => {
        this.loadData()
      }, 5000)
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    formatTime(date) {
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${h}:${m}:${s}`
    },
    animateNumber(key, target) {
      const start = this.animatedStats[key]
      const end = Math.round(target)
      if (start === end) return
      const duration = 600
      const startTime = performance.now()
      const step = (now) => {
        const elapsed = now - startTime
        const progress = Math.min(elapsed / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        this.animatedStats[key] = Math.round(start + (end - start) * eased)
        if (progress < 1) {
          requestAnimationFrame(step)
        }
      }
      requestAnimationFrame(step)
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
/* 简约风格 - 系统监控 */
.system-monitoring {
  padding: 20px;
  max-width: 1200px;
}

.refresh-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 0;
  border: 1px solid #e0e0e0;
}

.last-update {
  font-size: 14px;
  color: #666;
}

.refresh-btn {
  background: #000;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.refresh-btn:hover {
  background: #333;
}

.refresh-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.auto-refresh {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  margin-left: auto;
}

.auto-refresh input {
  cursor: pointer;
}

/* 统计卡片网格 - 简约风格 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.stat-card {
  background: #fff;
  border-radius: 0;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid #e0e0e0;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
}

.users-icon {
  background: white;
  color: #1976d2;
  border-color: #1976d2;
}

.agent-icon {
  background: white;
  color: #388e3c;
  border-color: #388e3c;
}

.customer-icon {
  background: white;
  color: #f57c00;
  border-color: #f57c00;
}

.memory-icon {
  background: white;
  color: #c2185b;
  border-color: #c2185b;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-top: 4px;
}

/* 区块卡片 - 简约风格 */
.section-card {
  background: #fff;
  border-radius: 0;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
}

.section-card h3 {
  margin: 0 0 16px;
  font-size: 16px;
  color: #333;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 服务器信息 */
.server-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-row:last-of-type {
  border-bottom: none;
}

.info-label {
  font-size: 14px;
  color: #666;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.memory-bar {
  height: 8px;
  background: #f0f0f0;
  border-radius: 0;
  overflow: hidden;
  margin-top: 8px;
}

.memory-progress {
  height: 100%;
  background: #000;
  border-radius: 0;
  transition: width 0.3s ease;
}

/* 用户列表 - 简约风格 */
.user-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fafafa;
  border-radius: 0;
  border: 1px solid #f0f0f0;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
}

.user-avatar.agent {
  background: white;
  color: #388e3c;
  border-color: #388e3c;
}

.user-avatar.customer {
  background: white;
  color: #f57c00;
  border-color: #f57c00;
}

.user-info {
  flex: 1;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.user-detail {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.user-status {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 2px;
  border: 1px solid;
}

.user-status.online {
  background: white;
  color: #388e3c;
  border-color: #388e3c;
}

.empty-list {
  text-align: center;
  padding: 30px;
  color: #999;
  font-size: 14px;
}

/* 域名管理 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  margin: 0;
}

.refresh-btn.small {
  padding: 6px 12px;
  font-size: 13px;
}

.domain-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.domain-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px;
  background: #fafafa;
  border-radius: 0;
  border: 1px solid #e0e0e0;
}

.domain-info {
  flex: 1;
}

.domain-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.blocked-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #ff4d4f;
  color: #fff;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 500;
}

.domain-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.domain-remark {
  font-size: 13px;
  color: #666;
  padding: 8px;
  background: #fff;
  border-radius: 0;
  margin-top: 8px;
  border: 1px solid #e0e0e0;
}

.remark-label {
  font-weight: 600;
  color: #333;
}

.domain-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  width: 36px;
  height: 36px;
  border: 1px solid;
  border-radius: 2px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background: white;
}

.action-btn.remark-btn {
  color: #1677ff;
  border-color: #1677ff;
}

.action-btn.remark-btn:hover {
  background: #1677ff;
  color: white;
}

.action-btn.block-btn {
  color: #ff4d4f;
  border-color: #ff4d4f;
}

.action-btn.block-btn:hover {
  background: #ff4d4f;
  color: white;
}

.action-btn.unblock-btn {
  color: #52c41a;
  border-color: #52c41a;
}

.action-btn.unblock-btn:hover {
  background: #52c41a;
  color: white;
}

.action-btn.landing-btn {
  color: #722ed1;
  border-color: #722ed1;
}

.action-btn.landing-btn:hover {
  background: #722ed1;
  color: white;
}

/* 弹窗样式 - 简约风格 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: #fff;
  border-radius: 0;
  width: 500px;
  max-width: 90vw;
  border: 1px solid #e0e0e0;
}

.modal-box.modal-large {
  width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: #999;
  cursor: pointer;
  border-radius: 4px;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-item {
  margin-bottom: 16px;
}

.form-item:last-child {
  margin-bottom: 0;
}

.form-item label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.domain-display {
  padding: 10px 12px;
  background: #f5f5f5;
  border-radius: 2px;
  font-size: 14px;
  color: #666;
  font-family: monospace;
  border: 1px solid #e0e0e0;
}

.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.form-textarea:focus {
  outline: none;
  border-color: #000;
}

.form-textarea-large {
  min-height: 300px;
  font-family: monospace;
  font-size: 13px;
}

.form-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  font-size: 14px;
  font-family: monospace;
  background: #f5f5f5;
}

.form-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  font-size: 14px;
  background: #fff;
  cursor: pointer;
}

.form-select:focus {
  outline: none;
  border-color: #000;
}

.hint {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

.landing-url-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.quick-actions {
  display: flex;
  gap: 12px;
}

.upload-btn {
  cursor: pointer;
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  padding: 8px 16px;
  border: 1px solid;
  border-radius: 2px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.btn-primary {
  background: #000;
  color: #fff;
  border-color: #000;
}

.btn-primary:hover {
  background: #333;
}

.btn-secondary {
  background: #fff;
  color: #333;
  border-color: #d9d9d9;
}

.btn-secondary:hover {
  background: #f5f5f5;
}

/* === 动画效果 === */

/* 心跳数字动画 */
.heartbeat {
  animation: heartbeat 2s ease-in-out infinite;
}

@keyframes heartbeat {
  0%, 100% { transform: scale(1); }
  15% { transform: scale(1.08); }
  30% { transform: scale(1); }
  45% { transform: scale(1.05); }
  60% { transform: scale(1); }
}

/* 图标脉冲动画 */
.pulse-icon {
  animation: pulseIcon 2.5s ease-in-out infinite;
}

@keyframes pulseIcon {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,0,0,0.15); }
  50% { box-shadow: 0 0 0 10px rgba(0,0,0,0); }
}

/* 在线状态脉冲点 */
.pulse-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #388e3c;
  margin-right: 5px;
  vertical-align: middle;
  animation: pulseDot 1.5s ease-in-out infinite;
}

@keyframes pulseDot {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(56,142,60,0.5); }
  50% { opacity: 0.6; box-shadow: 0 0 0 6px rgba(56,142,60,0); }
}

/* 线条绘制边框动画 */
.line-draw {
  position: relative;
  border-color: transparent;
}

.line-draw::before,
.line-draw::after {
  content: '';
  position: absolute;
  border: 1px solid #e0e0e0;
}

.line-draw::before {
  top: 0; left: 0; right: 0; bottom: 0;
  border-right: none; border-bottom: none;
  animation: lineDrawTL 0.6s ease-out forwards;
}

.line-draw::after {
  top: 0; left: 0; right: 0; bottom: 0;
  border-left: none; border-top: none;
  animation: lineDrawBR 0.6s 0.3s ease-out forwards;
}

@keyframes lineDrawTL {
  0% { clip-path: inset(0 100% 100% 0); }
  100% { clip-path: inset(0 0 0 0); }
}

@keyframes lineDrawBR {
  0% { clip-path: inset(100% 0 0 100%); }
  100% { clip-path: inset(0 0 0 0); }
}

/* 内存条发光动画 */
.memory-progress {
  animation: memoryGlow 2s ease-in-out infinite;
}

@keyframes memoryGlow {
  0%, 100% { box-shadow: 0 0 4px rgba(0,0,0,0.1); }
  50% { box-shadow: 0 0 12px rgba(0,0,0,0.3); }
}

/* 统计卡片悬停效果 */
.stat-card {
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}
</style>
